import { faClone } from '@fortawesome/free-regular-svg-icons';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Button, Card, CardBody, CardFooter, CardHeader, Col,
  Form, FormFeedback, FormGroup, FormText, Input, Label, Row,
} from 'reactstrap';
import styled from 'styled-components';
import isEmail from 'validator/es/lib/isEmail';
import isUUID from 'validator/es/lib/isUUID';

import { ClauseEdit, ClauseShow } from 'src/components/TradeRule/TimeOfUse';
import FlashesStore from 'src/stores/FlashesStore';
import {
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL, TRADE_DIRECTION_UNSPECIFIED,
} from 'src/util/constants';
import { getTradePointLabel } from 'src/util/i18n/tradeRule';
import isActive from 'src/util/isActive';
import { isFloatValueAndGTEQZero } from 'src/util/math';
import isDirectionValid from 'src/util/tradeDirection/validation';
import { isProposerTradePointIdValid } from 'src/util/tradeRule/validation';

import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import { areClausesValid } from '../helpers';

const AddClauseButton = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  align-items: flex-start
`;

const MissingClauseFeedback = styled.small`
  display: block;
  margin-top: 0.25rem;
`;

const TimeOfUsePricingFormFragment = graphql`
  fragment TradeRuleProposeNominatedTimeOfUsePricingForm_property on Property {
    id
    title
    timezone
    publicHolidayRegion
    meters {
      edges {
        node {
          id
          identifier
          title
          tradePointId
          active { start finish }
          residualRules: rules(first: 500, type: TRADE_TYPE_RESIDUAL, state: TRADE_RULE_STATE_ACCEPTED, start: $now, finish: $now) {
            edges {
              node {
                id
                priority
                tradeType
                state
                buyer {
                  userId
                  communityId
                  residualId
                  tradePoint {
                    id
                  }
                }
                seller {
                  userId
                  communityId
                  residualId
                  tradePoint {
                    id
                  }
                }
                clauses {
                  edges {
                    node {
                      price
                      ignorePublicHolidays
                      monthsOfYear
                      daysOfWeek
                      timesOfDay {
                        start  { hours minutes seconds }
                        finish { hours minutes seconds }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * TimeOfUsePricingForm component for proposing nominated time-of-use trade rules
 * @param {object} props
 * @param {object} props.property Property data containing timezone and public holiday
 * region
 * @param {object} props.router
 * @param {Function} props.onPricingButtonClick function handling the change of
 * state between the time of use pricing and flat pricing forms.
 * @param {Function} props.handleTradeRuleProposeNominated  Mutation handler for
 * ProposeNominatedTradeMutation
 * @param {boolean} props.processing whether the mutation is being processed.
 * @returns {React.ReactComponentElement} - TimeOfUsePricingForm component
 */
function TimeOfUsePricingForm(props) {
  let { property } = props;
  const {
    onPricingButtonClick, handleTradeRuleProposeNominated, processing, router,
  } = props;
  const intl = useIntl();

  property = useFragment(TimeOfUsePricingFormFragment, property);

  const [direction, setDirection] = useState(TRADE_DIRECTION_UNSPECIFIED);
  const [proposerTradePointId, setProposerTradePointId] = useState(null);
  const [recipientUserEmail, setRecipientUserEmail] = useState('');
  const [clauses, setClauses] = useState([]);
  const [ignoreDaylightSavings, setIgnoreDaylightSavings] = useState(false);
  const [ignorePublicHolidays, setIgnorePublicHolidays] = useState(false);

  const [directionValid, setDirectionValid] = useState(null);
  const [proposerTradePointIdValid, setProposerTradePointIdValid] = useState(null);
  const [recipientUserEmailValid, setRecipientUserEmailValid] = useState(null);
  const [missingClause, setMissingClause] = useState(false);

  const [editing, setEditing] = useState(null); // { index }

  const lastClauseIdRef = useRef(0);

  const mutationName = 'proposeNominatedTrade';

  const optionTradePointDefault = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.time_of_use_pricing_form.proposer_trade_point_id.default.label',
    defaultMessage: 'Select a Trade Point',
  });

  const optionDirectionBuy = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.time_of_use_pricing_form.direction.trade_direction_buy.label',
    defaultMessage: 'Buy',
  });

  const optionDirectionSell = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use_pricing_form.direction.trade_direction_sell.label',
    defaultMessage: 'Sell',
  });

  const optionDirectionDefault = intl.formatMessage(
    {
      id: 'trade_rule.trade_rule_propose_nominated_time_of_use_pricing_form.option.direction.default_option.label',
      defaultMessage: 'Select "{optionDirectionBuy}" or "{optionDirectionSell}"',
    },
    { optionDirectionBuy, optionDirectionSell },
  );

  const flatPricingLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.time_of_use_pricing_form.flat_pricing.button.label',
    defaultMessage: 'Flat pricing',
  });

  const proposeNominatedTradeTitle = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.propose_nominated_trade.label',
    defaultMessage: 'Propose a peer-to-peer trade',
  });

  const directionLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.direction.label',
    defaultMessage: 'Are you buying or selling?',
  });

  const invalidDirectionFeedback = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.direction.invalid_direction.feedback',
    defaultMessage: 'Invalid direction',
  });

  const directionHint = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.direction.hint',
    defaultMessage: 'Are you proposing to sell your energy, or buy energy from a friend?',
  });

  const invalidTradePointFeedback = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.trade_point.invalid_trade_point.feedback',
    defaultMessage: 'Invalid trade point selection',
  });

  const tradePointHint = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.tradepoint.hint',
    defaultMessage: 'Which trade point are you using?',
  });

  const recipientEmailLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.recipient_user_email.label',
    defaultMessage: 'Recipient email',
  });

  const invalidEmailFeedback = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.recipient_user_email.invalid_email.feedback',
    defaultMessage: 'Invalid email',
  });

  const priceHint = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.price.hint',
    defaultMessage: 'What price, in c/kWh, do you want to propose?',
  });

  const priceLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.price.label',
    defaultMessage: 'Price',
  });

  const addClauseLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.add_clause.label',
    defaultMessage: 'Add clause',
  });

  const missingClauseFeedback = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.missing_clause.feedback',
    defaultMessage: 'You must add at least one clause',
  });

  const ignoreDaylightSavingsLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.ignore_day_light_savings.label',
    defaultMessage: 'Ignore daylight savings',
  });

  const ignorePublicHolidaysLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.ignore_public_holidays.label',
    defaultMessage: 'Ignore public holidays',
  });

  const submitLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.submit.label',
    defaultMessage: 'Propose',
  });

  const cancelLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.cancel.label',
    defaultMessage: 'Cancel',
  });

  const copyFromTariffLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated_time_of_use.price.copy_from_tariff.label',
    defaultMessage: 'Copy from retailer default',
  });

  const generateClauseId = () => {
    lastClauseIdRef.current += 1;
    return lastClauseIdRef.current.toString();
  };

  const formatClauseFromEdge = (edge) => {
    const { node } = edge;
    const {
      price, monthsOfYear, daysOfWeek, timesOfDay,
    } = node;

    return {
      price,
      monthsOfYear,
      daysOfWeek,
      timesOfDay,
      id: generateClauseId(),
    };
  };

  const copyFromResidual = () => {
    const { meters } = property;

    if (!directionValid || !proposerTradePointIdValid) {
      return null;
    }

    const meterEdge = meters.edges
      .filter((edge) => edge.node.tradePointId === proposerTradePointId)[0];

    if (meterEdge === undefined) {
      return null;
    }

    const { tradePointId, residualRules } = meterEdge.node;

    let clausesData = [];
    const currentRules = residualRules.edges.filter((edge) => {
      if (direction === TRADE_DIRECTION_BUY) {
        return edge.node.buyer.tradePoint.id === tradePointId;
      }
      if (direction === TRADE_DIRECTION_SELL) {
        return edge.node.seller.tradePoint.id === tradePointId;
      }
      return false;
    });
    if (currentRules.length === 1) {
      clausesData = currentRules[0].node.clauses.edges.map(formatClauseFromEdge);
    }

    if (clausesData.length === 0) {
      return null;
    }

    let ignorePublicHolidaysData = false;
    if (residualRules.edges.length > 0) {
      const rule = residualRules.edges[0].node;
      if (rule.clauses.edges.length > 0) {
        const clause = rule.clauses.edges[0].node;
        if (clause.ignorePublicHolidays) {
          ignorePublicHolidaysData = true;
        }
      }
    }

    return { clauses: clausesData, ignorePublicHolidays: ignorePublicHolidaysData };
  };

  const validateDirection = () => {
    const directionValidValue = isDirectionValid(direction);
    setDirectionValid(directionValidValue);
  };

  const validateProposerTradePointId = () => {
    const proposerTradePointIdValidValue = isProposerTradePointIdValid(proposerTradePointId);
    setProposerTradePointIdValid(proposerTradePointIdValidValue);
  };

  const validateRecipientUserEmail = () => {
    const recipientUserEmailValidValue = isEmail(recipientUserEmail);
    setRecipientUserEmailValid(recipientUserEmailValidValue);
  };

  const validateClauses = () => {
    if (clauses.length === 0) {
      setMissingClause(true);
      return;
    }
    setMissingClause(false);
  };

  const isValid = directionValid !== null && directionValid
      && proposerTradePointIdValid !== null && proposerTradePointIdValid
      && recipientUserEmailValid !== null && recipientUserEmailValid
      && areClausesValid(clauses, ignorePublicHolidays)
      && clauses.every(({ price }) => isFloatValueAndGTEQZero(price));

  // Validate clauses and flash overlapping time errors only when clauses change
  // Separated from other field validations to prevent duplicate error flashes when
  // users edit unrelated form fields (proposerTradePointId, recipientUserEmail, direction)
  useEffect(() => {
    if (!areClausesValid(clauses, ignorePublicHolidays) && clauses?.length > 0) {
      const clausesOverlappingErrorMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_propose_nominated.trade_rule_propose_nominated_time_of_use_pricing_form.invalid_clauses.response_message.error', defaultMessage: 'Form data not valid. Clauses can not have overlapping times.' });
      FlashesStore.flash(FlashesStore.ERROR, clausesOverlappingErrorMsg, mutationName);
    }
  }, [clauses, ignorePublicHolidays]);

  // Validate other form fields (trade point, email, direction) independently
  // This effect updates validation state without triggering clause overlap error flashes
  useEffect(() => {
    if (proposerTradePointId) {
      setProposerTradePointIdValid(
        isProposerTradePointIdValid(proposerTradePointId),
      );
    }
    if (recipientUserEmail) {
      setRecipientUserEmailValid(
        isEmail(recipientUserEmail),
      );
    }
    if (direction !== TRADE_DIRECTION_UNSPECIFIED) {
      setDirectionValid(isDirectionValid(direction));
    }
  }, [proposerTradePointId, recipientUserEmail, direction]);

  const validateForm = () => {
    validateDirection();
    validateProposerTradePointId();
    validateRecipientUserEmail();
    validateClauses();
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const { timezone, publicHolidayRegion } = property;

    if (processing) {
      const processingMsg = intl.formatMessage({
        id: 'trade_rule.trade_rule_propose_nominated.time_of_use_pricing_form.form.state.invalid_still_processing',
        defaultMessage: 'We are still processing your request...',
      });
      FlashesStore.flash(FlashesStore.INFO, processingMsg, mutationName);
      return;
    }

    validateForm();

    if (!isValid) {
      const errorMsg = intl.formatMessage({
        id: 'trade_rule.trade_rule_propose_nominated.time_of_use_pricing_form.form.state.invalid_data',
        defaultMessage: 'Form data not valid. Please see below.',
      });
      FlashesStore.flash(FlashesStore.ERROR, errorMsg, mutationName);
      return;
    }

    const formatClause = (clause) => {
      const {
        price, monthsOfYear, daysOfWeek, timesOfDay,
      } = clause;

      return {
        price,
        timezone,
        publicHolidayRegion,
        ignoreDaylightSavings,
        ignorePublicHolidays,
        monthsOfYear,
        daysOfWeek,
        timesOfDay,
      };
    };

    const input = {
      direction,
      clauses: clauses.map(formatClause),
      proposerTradePointId,
      recipientUserEmail,
    };

    handleTradeRuleProposeNominated(input);
  };

  const handleDirectionChange = (event) => {
    const { value: directionValue } = event.target;
    const directionValidValue = isDirectionValid(directionValue);

    setDirection(directionValue);
    setDirectionValid(directionValidValue);
  };

  const handleRecipientUserEmailChange = (event) => {
    const { value: recipientUserEmailValue } = event.target;
    const recipientUserEmailValidValue = isEmail(recipientUserEmailValue);

    setRecipientUserEmail(recipientUserEmailValue);
    setRecipientUserEmailValid(recipientUserEmailValidValue);
  };

  const handleProposerTradePointIdChange = (event) => {
    const { value: proposerTradePointIdValue } = event.target;
    const proposerTradePointIdValidValue = isUUID(proposerTradePointIdValue);

    setProposerTradePointId(proposerTradePointIdValue);
    setProposerTradePointIdValid(proposerTradePointIdValidValue);
  };

  const handleIgnoreDaylightSavingsChange = (event) => {
    const { checked: ignoreDaylightSavingsValue } = event.target;
    setIgnoreDaylightSavings(ignoreDaylightSavingsValue);
  };

  const handleIgnorePublicHolidaysChange = (event) => {
    const { checked: ignorePublicHolidaysValue } = event.target;
    setIgnorePublicHolidays(ignorePublicHolidaysValue);
  };

  const isEditing = (index) => {
    if (index === undefined) {
      return !!editing;
    }

    return editing && editing.index === index;
  };

  const editClause = (index) => {
    setEditing({ index });
  };

  const addClause = () => {
    setEditing({ index: null });
  };

  const removeClause = (index) => {
    const newClauses = [...clauses];
    newClauses.splice(index, 1);
    setClauses(newClauses);
  };

  const editClauseSet = (index, newClause) => {
    const newClauses = [...clauses];
    newClauses[index] = { ...newClause, id: clauses[index].id };
    setClauses(newClauses);
    setEditing(null);
  };

  const addClauseSet = (newClause) => {
    const newClauses = [...clauses];
    newClauses.push({ ...newClause, id: generateClauseId() });
    setClauses(newClauses);
    setEditing(null);
    setMissingClause(false);
  };

  const cancelEdit = () => {
    setEditing(null);
  };

  const { meters } = property;
  const meterNodes = meters && meters.edges.map((edge) => (edge.node));

  const tradePointLabel = getTradePointLabel(intl, direction);

  return (
    <Form onSubmit={handleSubmit} disabled={processing}>
      <Card>
        <CardHeader className="d-flex flex-wrap">
          <h2 className="mb-0">
            {proposeNominatedTradeTitle}
          </h2>
          <Button color="darken" className="ms-auto" onClick={onPricingButtonClick}>
            {flatPricingLabel}
          </Button>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label for="direction">{directionLabel}</Label>
            <Input type="select" name="direction" id="direction" defaultValue={direction} onChange={handleDirectionChange} disabled={processing} valid={directionValid} invalid={directionValid !== null && !directionValid}>
              <option value={null}>{optionDirectionDefault}</option>
              <option value={TRADE_DIRECTION_BUY}>{optionDirectionBuy}</option>
              <option value={TRADE_DIRECTION_SELL}>{optionDirectionSell}</option>
            </Input>
            <FormFeedback>{invalidDirectionFeedback}</FormFeedback>
            <FormText>
              {directionHint}
            </FormText>
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="proposerTradePointId">{tradePointLabel}</Label>
                <Input type="select" name="select" id="proposerTradePointId" defaultValue={proposerTradePointId} onChange={handleProposerTradePointIdChange} disabled={processing} valid={proposerTradePointIdValid} invalid={proposerTradePointIdValid !== null && !proposerTradePointIdValid}>
                  <option key="meter-id-null">
                    {optionTradePointDefault}
                  </option>
                  {meterNodes && meterNodes.map((meter) => (
                    <option
                      value={meter.tradePointId}
                      disabled={!isActive(meter.active)}
                      key={meter.id}
                    >
                      {`${meter.title} (${meter.identifier})`}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{invalidTradePointFeedback}</FormFeedback>
                <FormText>
                  {tradePointHint}
                </FormText>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="recipientUserEmail">{recipientEmailLabel}</Label>
                <Input type="email" name="recipientUserEmail" id="recipientUserEmail" value={recipientUserEmail} onChange={handleRecipientUserEmailChange} disabled={processing} valid={recipientUserEmailValid} invalid={recipientUserEmailValid !== null && !recipientUserEmailValid} />
                <FormFeedback>{invalidEmailFeedback}</FormFeedback>
                <FormText>
                  {/* FormattedMessage required (not intl.formatMessage) for JSX component interpolation. See: https://formatjs.github.io/docs/react-intl/components/#rich-text-formatting */}
                  {/* intl.formatMessage() returns strings only, renders React components as [object Object]. See: https://formatjs.github.io/docs/react-intl/api/#usage */}
                  <FormattedMessage
                    id="trade_rule.trade_rule_propose_nominated.trade_rule_propose_nominated_time_of_use_pricing_form.recipient_user_email.hint.text"
                    defaultMessage="Who do you want to propose to trade with?"
                    values={{
                      link: (chunks) => (
                        <LinkInI18nBlock
                          url={intl.formatMessage({ id: 'trade_rule.trade_rule_propose_nominated.trade_rule_propose_nominated_time_of_use_pricing_form.recipient_user_email.hint.url', defaultMessage: 'trade_rule.trade_rule_propose_nominated.trade_rule_propose_nominated_time_of_use_pricing_form.recipient_user_email.hint.url' })}
                          urlKey="trade_rule.trade_rule_propose_nominated.trade_rule_propose_nominated_time_of_use_pricing_form.recipient_user_email.hint.url"
                          chunks={chunks}
                        />
                      ),
                    }}
                  />
                </FormText>
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            {clauses.map((clause, index) => {
              if (isEditing(index)) {
                return (
                  <ClauseEdit
                    clause={clause}
                    setClause={
                        (newClause) => editClauseSet(index, newClause)
                      }
                    cancelEdit={cancelEdit}
                    priceHelpText={priceHint}
                    priceLabel={priceLabel}
                    key={clause.id}
                  />
                );
              }
              return (
                <ClauseShow
                  clause={clause}
                  showControls={!isEditing()}
                  editClause={() => editClause(index)}
                  removeClause={() => removeClause(index)}
                  key={clause.id}
                />
              );
            })}
            {isEditing(null) && (
            <ClauseEdit
              setClause={(newClause) => addClauseSet(newClause)}
              cancelEdit={cancelEdit}
              priceHelpText={priceHint}
              priceLabel={priceLabel}
            />
            )}
            {!isEditing() && (
            <AddClauseButton>
              <Button color="darken" onClick={() => addClause()}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {addClauseLabel}
              </Button>

              {missingClause && (
              <MissingClauseFeedback className="text-danger">
                {missingClauseFeedback}
              </MissingClauseFeedback>
              )}

            </AddClauseButton>
            )}
          </FormGroup>
          <FormGroup>
            <Label for="ignoreDaylightSavings">
              {ignoreDaylightSavingsLabel}
              <Input
                type="checkbox"
                checked={ignoreDaylightSavings}
                className="mb-2"
                disabled={processing}
                id="ignoreDaylightSavings"
                label="Ignore daylight savings"
                name="ignoreDaylightSavings"
                onChange={handleIgnoreDaylightSavingsChange}
                value={`${ignoreDaylightSavings}`}
              />
            </Label>
            <Label for="ignorePublicHolidays">
              {ignorePublicHolidaysLabel}
              <Input
                type="checkbox"
                checked={ignorePublicHolidays}
                className="mb-2"
                disabled={processing}
                id="ignorePublicHolidays"
                label="Ignore public holidays"
                name="ignorePublicHolidays"
                onChange={handleIgnorePublicHolidaysChange}
                value={`${ignorePublicHolidays}`}
              />
            </Label>
          </FormGroup>
        </CardBody>
        <CardFooter className="d-flex">
          <Button disabled={processing || isEditing() || !isValid}>
            {submitLabel}
          </Button>
          <Button color="" onClick={() => (router.go(-1))} disabled={processing || isEditing()}>
            {cancelLabel}
          </Button>
          <Button
            color="darken"
            className="ms-auto"
            onClick={() => {
              const result = copyFromResidual();
              if (result) {
                setClauses(result.clauses);
                setIgnorePublicHolidays(result.ignorePublicHolidays);
              }
            }}
            disabled={processing || isEditing() || !copyFromResidual()}
          >
            <FontAwesomeIcon icon={faClone} className="me-2" />
            {copyFromTariffLabel}
          </Button>
        </CardFooter>
      </Card>
    </Form>
  );
}

TimeOfUsePricingForm.propTypes = {
  handleTradeRuleProposeNominated: PropTypes.func,
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  processing: PropTypes.bool,
  router: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  onPricingButtonClick: PropTypes.func.isRequired,
};

TimeOfUsePricingForm.defaultProps = {
  handleTradeRuleProposeNominated: null,
  property: null,
  processing: false,
};

export default TimeOfUsePricingForm;
